import { useState, useEffect, useCallback, useRef } from 'react'
import { useAccessibilitySession } from './accessibility-session'

/**
 * Options for the shared timed terminal hook.
 *
 * @since 1.0.0
 */
export interface UseTimedTerminalOptions {
  /** Initial countdown duration in seconds */
  initialSeconds: number
  /** Additional seconds granted when patient requests more time. Default: 60 */
  extensionSeconds?: number
  /** Called when the countdown expires (after any extensions) */
  onExpire: () => void
  /**
   * Whether this timed terminal should route through the shared
   * reset/reassurance overlay on expiry. Default: true
   */
  useReassuranceOverlay?: boolean
  /** Temporarily stop countdown ticks without changing the remaining value. */
  paused?: boolean
}

/**
 * Return value from the timed terminal hook.
 *
 * @since 1.0.0
 */
export interface TimedTerminalState {
  /** Current countdown value in seconds */
  countdown: number
  /** Whether the patient has extended at least once */
  hasExtended: boolean
  /** Extend the countdown by the configured extension amount */
  extendTime: () => void
  /** Whether the countdown has reached zero */
  isExpired: boolean
  /** Restart the terminal countdown from a fresh duration. */
  restart: (seconds?: number) => void
}

/**
 * Shared timed terminal hook for countdown, "more time", and reassurance integration.
 *
 * Terminal-state screens use this hook instead of implementing their own
 * countdown logic. When the countdown expires, the hook routes through
 * the shared reset/reassurance overlay if configured.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * function DoneStep({ onComplete }: { onComplete: () => void }) {
 *   const { countdown, extendTime, hasExtended } = useTimedTerminal({
 *     initialSeconds: 90,
 *     onExpire: onComplete,
 *   })
 *
 *   return (
 *     <div>
 *       <CountdownDisplay seconds={countdown} />
 *       <Button onClick={extendTime}>More time</Button>
 *     </div>
 *   )
 * }
 * ```
 */
export function useTimedTerminal({
  initialSeconds,
  extensionSeconds = 60,
  onExpire,
  useReassuranceOverlay = true,
  paused = false,
}: UseTimedTerminalOptions): TimedTerminalState {
  const { triggerReset, onExtendTime } = useAccessibilitySession()
  const [countdown, setCountdown] = useState(initialSeconds)
  const [hasExtended, setHasExtended] = useState(false)
  const countdownRef = useRef(initialSeconds)
  const isExpiredRef = useRef(false)
  const onExpireRef = useRef(onExpire)
  onExpireRef.current = onExpire

  // Extend the countdown
  const extendTime = useCallback(() => {
    if (isExpiredRef.current || paused) return

    countdownRef.current += extensionSeconds
    setCountdown(countdownRef.current)
    setHasExtended(true)
  }, [extensionSeconds, paused])

  const restart = useCallback((seconds = initialSeconds) => {
    countdownRef.current = seconds
    isExpiredRef.current = false
    setCountdown(seconds)
    setHasExtended(false)
  }, [initialSeconds])

  // Register for shell-level extend-time signals
  useEffect(function runUseTimedTerminalEffect() {
    return onExtendTime(extendTime)
  }, [onExtendTime, extendTime])

  // Tick the countdown
  useEffect(function syncInterval() {
    if (isExpiredRef.current || paused) return

    const interval = setInterval(() => {
      if (countdownRef.current <= 1) {
        countdownRef.current = 0
        isExpiredRef.current = true
        setCountdown(0)
        clearInterval(interval)

        if (useReassuranceOverlay) {
          triggerReset('timeout')
        } else {
          onExpireRef.current()
        }
        return
      }

      countdownRef.current -= 1
      setCountdown(countdownRef.current)
    }, 1000)

    return function clearIntervalTimer() {
      return clearInterval(interval)
    }
  }, [paused, triggerReset, useReassuranceOverlay])

  return { countdown, hasExtended, extendTime, isExpired: isExpiredRef.current, restart }
}
